'use client'

import { useState } from 'react'
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'

type TransakCheckoutPanelProps = {
  amount: string
  productId: number | string
  sellerId: number | string
  userId?: number | string | null
  customerEmail?: string | null
  walletAddress: string
  chain: string
  token: string
}

export function TransakCheckoutPanel({
  amount,
  productId,
  sellerId,
  userId,
  customerEmail,
  walletAddress,
  chain,
  token,
}: TransakCheckoutPanelProps) {
  const [loading, setLoading] = useState(false)
  const [widgetUrl, setWidgetUrl] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string | null>(null)

  async function startTransakCheckout() {
    try {
      setLoading(true)

      if (!walletAddress || !chain || !token) {
        toast.error('Este vendedor no tiene una wallet compatible configurada.')
        return
      }

      const res = await fetch('/api/payments/transak/create-widget-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          productId,
          sellerId,
          userId,
          customerEmail,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(json?.error || 'No se pudo iniciar Transak')
      }

      if (!json?.widgetUrl) {
        throw new Error('Transak no regresó widgetUrl')
      }

      setWidgetUrl(json.widgetUrl)
      setOrderId(json.orderId || null)
    } catch (error: any) {
      console.error('startTransakCheckout error:', error)
      toast.error(error?.message || 'No se pudo iniciar Transak')
    } finally {
      setLoading(false)
    }
  }

  if (widgetUrl) {
    return (
      <div className="space-y-3 rounded-xl border p-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Checkout seguro con tarjeta</p>
            <p className="text-xs text-muted-foreground">
              Completa el pago sin salir de esta página.
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setWidgetUrl(null)
              setOrderId(null)
            }}
          >
            Cambiar método
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border bg-background">
          <iframe
            src={widgetUrl}
            title="Transak Checkout"
            className="h-[720px] w-full"
            allow="camera; microphone; payment; clipboard-read; clipboard-write"
          />
        </div>

        {orderId && (
          <p className="text-center text-xs text-muted-foreground">
            Orden: {orderId}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4 rounded-xl border p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-primary/10 p-2 text-primary">
          <CreditCard className="h-5 w-5" />
        </div>

        <div className="space-y-1">
          <p className="font-medium">Paga con tarjeta</p>
          <p className="text-sm text-muted-foreground">
            Tu pago se convertirá a crypto y será enviado directamente a la
            wallet de cobro del vendedor.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-muted/40 p-3 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">Monto</span>
          <span className="font-medium">${amount} USD</span>
        </div>

        <div className="mt-1 flex justify-between gap-3">
          <span className="text-muted-foreground">Token</span>
          <span className="font-medium">{token || 'USDT'}</span>
        </div>

        <div className="mt-1 flex justify-between gap-3">
          <span className="text-muted-foreground">Red</span>
          <span className="font-medium">{chain || 'BSC'}</span>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border bg-background p-3 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Es posible que el proveedor solicite verificación de identidad,
          dependiendo del país, monto y método de pago.
        </p>
      </div>

      <Button
        type="button"
        className="w-full"
        onClick={startTransakCheckout}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Iniciando...
          </>
        ) : (
          'Continuar con tarjeta'
        )}
      </Button>
    </div>
  )
}